import { useId } from 'react';
import { cn } from '@/lib/cn';

export interface NexaGlyphProps {
  /** `color` — the logo as printed (navy, blue, cyan) for light surfaces; `light` —
   *  white with the cyan core, for the navy surfaces (the «Lo que sigue» card). */
  tone?: 'color' | 'light';
  className?: string;
}

/**
 * The Nexa mark as a plain, static, freely-sized glyph (the logo's own five shapes,
 * apps/landing/public/assets/nexa-logo.svg) — for the places the chrome lockup's fixed
 * 30px `NexaMark` does not fit: the copilot's face and the card's watermark. Sized by
 * the caller (`className`), decorative (`aria-hidden`). The animated mark stays
 * `NexaMark`.
 */
export function NexaGlyph({ tone = 'color', className }: NexaGlyphProps) {
  const uid = useId().replace(/:/g, '');
  const light = tone === 'light';
  const ink = light ? '#ffffff' : '#07213f';
  return (
    <svg
      viewBox="0 0 650 600"
      className={cn('block', className)}
      aria-hidden
      data-testid="nexa-glyph"
    >
      {light ? null : (
        <defs>
          <linearGradient
            id={`nxa-${uid}`}
            x1="430.07"
            y1="419.04"
            x2="430.07"
            y2="124.76"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#0166aa" />
            <stop offset="1" stopColor="#0181cb" />
          </linearGradient>
          <linearGradient
            id={`nxb-${uid}`}
            x1="112.47"
            y1="163.43"
            x2="600.93"
            y2="163.43"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#061e3c" />
            <stop offset="0.52" stopColor="#043359" />
            <stop offset="1" stopColor="#0187d0" />
          </linearGradient>
        </defs>
      )}
      <path
        fill={ink}
        d="M478.47,437.89l-158.49-49.98-180.32,144.87,151-185.07c6.23,2.47,13.02,3.82,20.12,3.82,10.29,0,19.91-2.84,28.13-7.78l139.56,94.14Z"
      />
      <path
        fill={ink}
        d="M126.42,518.69l95.91-204.58-101.28-133.14,136.35,104c-.85,3.82-1.3,7.8-1.3,11.88,0,13.32,4.76,25.53,12.68,35.01l-142.36,186.83Z"
      />
      <path
        fill={light ? '#05b8ec' : `url(#nxa-${uid})`}
        d="M398.5,291.94l77.47,127.09-116.48-97.3c3.81-7.46,5.97-15.92,5.97-24.88,0-9.82-2.59-19.04-7.12-27,85.55-105.46,142.56-144.49,143.45-145.09-14.98,19.09-103.29,167.18-103.29,167.18Z"
      />
      <path
        fill={light ? ink : `url(#nxb-${uid})`}
        d="M525.83,127.81l5.77-27.76c-90.37,23.03-180.75,132.64-193.97,149.17-7.93-4.48-17.09-7.04-26.85-7.04-15.82,0-30.07,6.72-40.06,17.47l-158.25-105.1,202.06,53.82c140-117.82,214.77-120.44,214.77-120.44,0-1.38-11.31-20.71-11.31-20.71l82.94,15.67c-40.09,15.43-75.1,44.92-75.1,44.92Z"
      />
      <path
        fill="#05b8ec"
        d="M342.65,296.85c0,17.6-14.27,31.87-31.87,31.87s-31.86-14.27-31.86-31.87,14.26-31.86,31.86-31.86,31.87,14.26,31.87,31.86Z"
      />
    </svg>
  );
}
